import Logo from "@/components/Logo";
import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, TrendingDown, Trash2, ImagePlus, Loader2, Plus, X, Brain, Percent, Scale, Trophy, Sparkles,
} from "lucide-react";
import { FeatureGate } from "@/components/FeatureGate";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const j = async (path, opts = {}) => {
  const r = await fetch(`${API}/journal${path}`, { credentials: "include", ...opts });
  const d = r.status === 204 ? null : await r.json().catch(() => null);
  if (!r.ok) throw new Error((d && d.detail) || "Request failed");
  return d;
};

const EMOTIONS = ["disciplined", "confident", "calm", "neutral", "fear", "greed", "revenge", "fomo"];
const RESULTS = [{ id: "win", label: "Win" }, { id: "loss", label: "Loss" }, { id: "be", label: "Break-even" }];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-card p-5" data-testid={`journal-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color }} strokeWidth={1.75} />
        <span className="section-label text-[8px] text-zinc-500">{label}</span>
      </div>
      <div className="mt-2 font-heading text-2xl font-light">{value}</div>
    </div>
  );
}

function TradeForm({ onClose, onCreated }) {
  const [f, setF] = useState({ symbol: "", direction: "long", entry: "", exit: "", rr: "", result: "win", emotion: "disciplined", notes: "" });
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [uploading, setUploading] = useState("");
  const [busy, setBusy] = useState(false);
  const beforeRef = useRef();
  const afterRef = useRef();

  const pickImage = (which) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(which);
    try {
      const fd = new FormData(); fd.append("file", file);
      const d = await j("/upload", { method: "POST", body: fd });
      const img = { path: d.url, preview: URL.createObjectURL(file) };
      (which === "before" ? setBefore : setAfter)(img);
    } catch (err) { toast.error(err.message); } finally { setUploading(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.symbol.trim()) { toast("Enter a symbol"); return; }
    setBusy(true);
    try {
      const d = await j("/trades", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...f, symbol: f.symbol.toUpperCase(),
          entry: parseFloat(f.entry) || 0, exit: parseFloat(f.exit) || 0, rr: parseFloat(f.rr) || 0,
          before_image: before?.path || null, after_image: after?.path || null,
        }),
      });
      onCreated(d); onClose();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const inp = "w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-white/25";
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-6 py-10 overflow-y-auto" data-testid="trade-form-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-lg glass-card p-6 space-y-3">
        <div className="flex items-center justify-between"><span className="font-heading text-lg font-light">Log a trade</span><button type="button" onClick={onClose}><X className="h-5 w-5 text-zinc-400" /></button></div>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Symbol (XAUUSD)" value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value.toUpperCase() })} className={inp} data-testid="trade-symbol" />
          <select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} className={inp} data-testid="trade-direction">
            <option value="long" className="bg-[#0A0A0A]">Long</option>
            <option value="short" className="bg-[#0A0A0A]">Short</option>
          </select>
          <input type="number" step="any" placeholder="Entry" value={f.entry} onChange={(e) => setF({ ...f, entry: e.target.value })} className={inp} data-testid="trade-entry" />
          <input type="number" step="any" placeholder="Exit" value={f.exit} onChange={(e) => setF({ ...f, exit: e.target.value })} className={inp} data-testid="trade-exit" />
          <input type="number" step="any" placeholder="Risk-reward (2.0)" value={f.rr} onChange={(e) => setF({ ...f, rr: e.target.value })} className={inp} data-testid="trade-rr" />
          <select value={f.result} onChange={(e) => setF({ ...f, result: e.target.value })} className={inp} data-testid="trade-result">
            {RESULTS.map((r) => <option key={r.id} value={r.id} className="bg-[#0A0A0A]">{r.label}</option>)}
          </select>
        </div>
        <select value={f.emotion} onChange={(e) => setF({ ...f, emotion: e.target.value })} className={inp} data-testid="trade-emotion">
          {EMOTIONS.map((em) => <option key={em} value={em} className="bg-[#0A0A0A]">{em[0].toUpperCase() + em.slice(1)}</option>)}
        </select>
        <textarea rows={3} placeholder="What did you do, why, and did you follow your plan?" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inp} data-testid="trade-notes" />
        <div className="flex flex-wrap gap-3">
          {[["before", before, setBefore], ["after", after, setAfter]].map(([which, img, setImg]) => (
            img && <div key={which} className="relative inline-block">
              <img src={img.preview} alt={which} className="max-h-24 rounded-lg border border-white/10" />
              <span className="absolute bottom-1 left-1 section-label text-[7px] bg-black/70 rounded px-1.5 py-0.5 text-zinc-300">{which}</span>
              <button type="button" onClick={() => setImg(null)} className="absolute -top-2 -right-2 bg-[#0A0A0A] border border-white/20 rounded-full p-1"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => beforeRef.current?.click()} disabled={!!uploading} className="btn-glass text-white px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="trade-upload-before">
            {uploading === "before" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} Before screenshot
          </button>
          <button type="button" onClick={() => afterRef.current?.click()} disabled={!!uploading} className="btn-glass text-white px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="trade-upload-after">
            {uploading === "after" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} After screenshot
          </button>
          <input ref={beforeRef} type="file" accept="image/*" className="hidden" onChange={pickImage("before")} />
          <input ref={afterRef} type="file" accept="image/*" className="hidden" onChange={pickImage("after")} />
          <button type="submit" disabled={busy} className="btn-solid px-5 py-2 text-sm ml-auto disabled:opacity-60" data-testid="trade-submit">{busy ? "Saving…" : "Save trade"}</button>
        </div>
      </form>
    </div>
  );
}

function AiReviewPanel({ review }) {
  return (
    <div className="mt-3 border-t border-white/8 pt-3" data-testid="journal-ai-review">
      <div className="flex flex-wrap items-center gap-2">
        <span className="section-label text-[9px] text-[#9fd3ff]">MBM Journal Review</span>
        <span className={`section-label text-[8px] rounded-full px-2 py-0.5 border ${review.followed_plan === "followed" ? "text-[#7ee0a1] border-[#7ee0a1]/30" : review.followed_plan === "deviated" ? "text-[#e6c67a] border-[#e6c67a]/30" : "text-zinc-400 border-white/15"}`}>
          {review.followed_plan === "followed" ? "✓ Followed plan" : review.followed_plan === "deviated" ? "⚠ Deviated from plan" : "Plan unclear"}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-2">
        {review.scores.map((s) => (
          <div key={s.key}>
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-zinc-300">{s.label}</span>
              <span className={s.score >= 7 ? "text-[#7ee0a1]" : s.score >= 4 ? "text-[#e6c67a]" : "text-[#ff7a7a]"}>{s.score}/10</span>
            </div>
            <div className="mt-0.5 h-1 rounded-full bg-white/8"><div className="h-1 rounded-full bg-[#9fd3ff]" style={{ width: `${s.score * 10}%` }} /></div>
          </div>
        ))}
      </div>
      {review.plan_comparison && <p className="mt-3 text-[12px] text-zinc-400 font-light">{review.plan_comparison}</p>}
      {review.deviation_question && <p className="mt-1.5 text-[12px] text-[#e6c67a] font-light italic">{review.deviation_question}</p>}
      <div className="mt-3 grid sm:grid-cols-3 gap-2 text-[11px]">
        <div className="rounded-lg border border-[#7ee0a1]/20 bg-[#7ee0a1]/5 p-2 text-zinc-300"><span className="text-[#7ee0a1]">Did well: </span>{review.did_well}</div>
        <div className="rounded-lg border border-[#e6c67a]/20 bg-[#e6c67a]/5 p-2 text-zinc-300"><span className="text-[#e6c67a]">Improve: </span>{review.improve}</div>
        <div className="rounded-lg border border-[#9fd3ff]/20 bg-[#9fd3ff]/5 p-2 text-zinc-300"><span className="text-[#9fd3ff]">Next trade: </span>{review.next_lesson}</div>
      </div>
    </div>
  );
}

function JournalBody() {
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [openReview, setOpenReview] = useState(null);

  const runReview = async (t) => {
    if (t.ai_review) { setOpenReview(openReview === t.id ? null : t.id); return; }
    setReviewing(t.id);
    try {
      const d = await j(`/trades/${t.id}/review`, { method: "POST" });
      setTrades((ts) => ts.map((x) => (x.id === t.id ? { ...x, ai_review: d } : x)));
      setOpenReview(t.id);
      toast.success("Journal review complete");
    } catch (e) { toast.error(e.message); }
    setReviewing(null);
  };

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([j("/trades"), j("/stats")]);
      setTrades(t); setStats(s);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await j(`/trades/${id}`, { method: "DELETE" }); setTrades((ts) => ts.filter((t) => t.id !== id)); j("/stats").then(setStats); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="section-label text-[10px] text-[#9fd3ff]">Trading Journal</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-light tracking-tight">Your process, on the record.</h1>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-solid px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2" data-testid="add-trade-btn">
          <Plus className="h-4 w-4" /> Log a trade
        </button>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Percent} label="Win Rate" value={`${stats.win_rate}%`} color="#7ee0a1" />
          <StatCard icon={Scale} label="Avg RR" value={stats.avg_rr} color="#9fd3ff" />
          <StatCard icon={Trophy} label="Trades" value={stats.total} color="#e6c67a" />
          <StatCard icon={Brain} label="Psychology" value={`${stats.psychology_score}/100`} color="#ff9d5c" />
        </div>
      )}

      <div className="mt-8 space-y-3" data-testid="trades-list">
        {trades.length === 0 && <div className="glass-card p-10 text-center text-sm text-zinc-500" data-testid="journal-empty">No trades logged yet. Every entry builds your discipline.</div>}
        {trades.map((t) => (
          <div key={t.id} className="glass-card p-4" data-testid="trade-row">
            <div className="flex items-center gap-4">
            <span className={`h-9 w-9 rounded-full grid place-items-center ${t.direction === "long" ? "text-[#7ee0a1]" : "text-[#ff7a7a]"}`} style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
              {t.direction === "long" ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-zinc-100 font-medium">{t.symbol}</span>
                <span className={`section-label text-[7px] rounded px-1.5 py-0.5 ${t.result === "win" ? "text-[#7ee0a1] border border-[#7ee0a1]/30" : t.result === "loss" ? "text-[#ff7a7a] border border-[#ff7a7a]/30" : "text-zinc-400 border border-white/15"}`}>{t.result}</span>
                <span className="text-[10px] text-zinc-600">{t.emotion}</span>
              </div>
              {t.notes && <div className="text-[12px] text-zinc-500 font-light truncate mt-0.5">{t.notes}</div>}
            </div>
            <div className="text-right text-[11px] text-zinc-500">
              <div>{t.entry} → {t.exit}</div>
              {t.rr ? <div className="text-[#9fd3ff]">{t.rr}R</div> : null}
            </div>
            {[["before_image", "B"], ["after_image", "A"], ["image_url", ""]].map(([k, tag]) =>
              t[k] ? (
                <div key={k} className="relative">
                  <img src={`${API}/journal/media/${t[k]}`} alt="chart" className="h-12 w-16 object-cover rounded border border-white/8 cursor-pointer" onClick={() => window.open(`${API}/journal/media/${t[k]}`, "_blank")} data-testid="trade-image" />
                  {tag && <span className="absolute bottom-0.5 left-0.5 text-[8px] bg-black/70 rounded px-1 text-zinc-300">{tag}</span>}
                </div>
              ) : null
            )}
            <button onClick={() => runReview(t)} disabled={reviewing === t.id}
              className={`inline-flex items-center gap-1.5 text-[11px] px-3 py-2 rounded-lg border transition-colors disabled:opacity-60 ${t.ai_review ? "border-[#9fd3ff]/40 text-[#9fd3ff]" : "border-white/15 text-zinc-300 hover:border-white/30"}`}
              data-testid="trade-ai-review-btn">
              {reviewing === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {reviewing === t.id ? "Reviewing…" : t.ai_review ? "AI Review" : "Get AI Review"}
            </button>
            <button onClick={() => del(t.id)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid="trade-delete"><Trash2 className="h-4 w-4" /></button>
            </div>
            {t.ai_review && openReview === t.id && <AiReviewPanel review={t.ai_review} />}
          </div>
        ))}
      </div>

      {showForm && <TradeForm onClose={() => setShowForm(false)} onCreated={(d) => { setTrades((ts) => [d, ...ts]); j("/stats").then(setStats); }} />}
    </>
  );
}

export default function Journal() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
            <span className="text-zinc-700">/</span>
            <span className="section-label text-[10px] text-[#9fd3ff]">Journal</span>
          </div>
          <Link to="/learn" className="text-xs text-zinc-400 hover:text-white transition-colors">Trainer</Link>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        <FeatureGate feature="journal">
          <JournalBody />
        </FeatureGate>
      </main>
    </div>
  );
}
